/**
 * Lookup Service
 *
 * IndexedDB caching for lookup/reference data. Uses centralized schema registry for deterministic versioning.
 *
 * Flow:
 *
 * 1. App Start: init() initializes IndexedDB with pre-defined schema from LOOKUP_TABLES
 * 2. First get('icd-codes'): Fetch data from API, cache in pre-registered IndexedDB table
 * 3. Subsequent calls: Read from IndexedDB (no API call)
 * 4. After 1 week: Data expires, refetch on next access
 *
 * Table Registration:
 *
 * - Tables must be pre-registered in schema-registry.ts (LOOKUP_TABLES)
 * - Live-only lookups can be supported without IndexedDB persistence
 * - Bump LOOKUP_SCHEMA_VERSION when adding new tables
 *
 * @example
 *   ```typescript
 *   const icdCodes = await lookupService.get('icd-codes');
 *   const results = await lookupService.search('icd-codes', 'diabetes', 10);
 *   ```;
 */

import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { LOOKUP_API_BASE_URL, LOOKUP_SEARCH_CONFIG } from '../http/api-client.tokens';
import { IndexedDBStore } from './indexed-db.store';
import {
  LOOKUP_SEARCH_SUPERSEDED,
  LookupSearchCoordinatorService,
  LookupWorkerCondition,
  LookupWorkerSearchConfig,
} from './lookup-search-coordinator.service';
import { matchesLookupSearch } from './lookup-search-predicate';
import { matchesSubstringSearch } from './lookup-substring-search';
import {
  getLookupTableSchema,
  getRegisteredLookupTableNames as getSchemaLookupTableNames,
  isRegisteredLookupTable,
  normalizeLookupName,
} from './schema-registry';

import {
  IcdCodeMetadata,
  LiveLookupConfig,
  LookupConfig,
  LookupSearchConfig,
  LookupSearchFilter,
  LookupSearchMode,
  LookupSearchPageRequest,
  LookupSearchPageResponse,
  LookupSyncTableProgress,
} from './lookup.types';

// =============================================================================
// Constants
// =============================================================================

const MAX_RETRIES = 3; // Max retry attempts for API failures
const LOOKUP_SEARCH_MODE_OVERRIDE_STORAGE_KEY = 'symphony.lookupSearch.mode';

/** Reserved metadata key (not a real lookup table) recording that a sync completed. */
const LOOKUP_SYNC_COMPLETE_MARKER = '__lookup_sync_complete__';

/**
 * Medication lookups that are intentionally live-only due to very large row counts. These endpoints are supported but
 * are not persisted in IndexedDB.
 */
const LIVE_API_ONLY_LOOKUPS = new Set([
  'drug-allergies',
  'drug-side-effects',
  'drug-drug-interactions',
  'drug-medication-details',
  'drug-dosing',
  'drug-conditions',
  'loinc-codes',
]);

/**
 * Lookup tables that should always read from IndexedDB first when cached. API is only used to seed IndexedDB when table
 * is missing/empty.
 */
const INDEXEDDB_FIRST_LOOKUPS = new Set([
  'icd-codes',
  'cpt-codes',
  'cpt2-codes',
  'modifier-codes',
  'drug-codes',
  'dose-types',
  'medication-statuses',
  'compliance-statuses',
  'reasons',
  'states',
  'plan-type-lobs',
  'appointment-statuses',
  'intervention-types',
  'outcome-gaps',
  'association-types',
  'treatment-plan-options',
]);

/** Administrative lists that must reflect server changes during online use. */
const NETWORK_FIRST_LOOKUPS = new Set(['clients', 'vendors']);

interface WorkerSearchTableConfig {
  chunkSize: number;
  rangeStrategy?: LookupWorkerSearchConfig['rangeStrategy'];
  searchFields: string[];
  getConditions?: (filterContext?: LookupSearchFilter) => LookupWorkerCondition[];
}

function shouldFilterIcdByLob(filterContext?: LookupSearchFilter): boolean {
  return filterContext?.controlType === 'cedis' && filterContext.rowStatus === 'ACTIVE_TODAY';
}

function getIcdWorkerConditions(filterContext?: LookupSearchFilter): LookupWorkerCondition[] {
  const conditions: LookupWorkerCondition[] = [];
  const requestedLob = filterContext?.lobName?.trim();
  if (requestedLob && shouldFilterIcdByLob(filterContext)) {
    conditions.push({
      fields: ['lobName', 'LobName'],
      operator: 'equalsIgnoreCase',
      value: requestedLob,
      allowMissing: true,
    });
  }
  if (
    filterContext?.isShow === true ||
    filterContext?.origin?.toLowerCase() === 'stage' ||
    filterContext?.origin?.toLowerCase() === 'orchestra'
  ) {
    conditions.push({ fields: ['isShow', 'IsShow'], operator: 'notFalse', allowMissing: true });
  }
  if (filterContext?.dateToFilter) {
    conditions.push({
      fields: ['termDate', 'TermDate'],
      operator: 'dateAfter',
      value: filterContext.dateToFilter,
      allowMissing: true,
    });
    conditions.push({
      fields: ['effectiveDate', 'EffectiveDate'],
      operator: 'dateOnOrBefore',
      value: filterContext.dateToFilter,
      allowMissing: true,
    });
  }
  if (filterContext?.rowStatus === 'ACTIVE_TODAY' && filterContext.hccCode != null) {
    conditions.push({ fields: ['hccCode', 'HccCode'], operator: 'hccEquals', value: filterContext.hccCode });
  }
  return conditions;
}

/** The only lookup tables enabled for parallel Web Worker substring search. */
const WORKER_SEARCH_TABLES: Readonly<Record<string, WorkerSearchTableConfig>> = {
  'icd-codes': {
    chunkSize: 35_000,
    searchFields: ['code', 'Code', 'icdDescription', 'IcdDescription', 'description', 'Description'],
    getConditions: getIcdWorkerConditions,
  },
  'drug-codes': {
    chunkSize: 35_000,
    searchFields: ['medicationName', 'MedicationName'],
  },
};

// =============================================================================
// Lookup Service
// =============================================================================

@Injectable({ providedIn: 'root' })
export class LookupService {
  private readonly http = inject(HttpClient);
  private readonly store = inject(IndexedDBStore);
  private readonly lookupSearchCoordinator = inject(LookupSearchCoordinatorService);
  private readonly injectedBaseUrl = inject(LOOKUP_API_BASE_URL, { optional: true }) ?? '';
  private readonly lookupSearchConfig = inject(LOOKUP_SEARCH_CONFIG, { optional: true });

  constructor() {
    // Store URL in globalThis immediately so MFE instances can access it
    if (this.injectedBaseUrl) {
      LookupService.global.baseUrl = this.injectedBaseUrl;
    }
  }

  private isLiveApiOnlyLookup(lookupName: string): boolean {
    return LIVE_API_ONLY_LOOKUPS.has(normalizeLookupName(lookupName));
  }

  private isSupportedLookup(lookupName: string): boolean {
    const canonical = normalizeLookupName(lookupName);
    return isRegisteredLookupTable(canonical) || this.isLiveApiOnlyLookup(canonical);
  }

  private isIndexedDbFirstLookup(lookupName: string): boolean {
    return INDEXEDDB_FIRST_LOOKUPS.has(normalizeLookupName(lookupName));
  }

  private isNetworkFirstLookup(lookupName: string): boolean {
    return NETWORK_FIRST_LOOKUPS.has(normalizeLookupName(lookupName));
  }

  // ===========================================================================
  // Global state - shared via globalThis to work across MFE bundles
  // ===========================================================================

  private static get global(): {
    initialized: boolean;
    initFailed: boolean;
    retryCount: number;
    pendingRequests: Map<string, Promise<any[]>>;
    baseUrl: string;
    /** In-memory cache for when IndexedDB is disabled (avoids repeated API calls) */
    memoryCache: Map<string, any[]>;
    /** Case-insensitive query cache for drug-codes contains search results. */
    drugCodesSearchCache: Map<string, any[]>;
    /** Deferred lookup metadata commits for end-of-run flush. */
    deferredLookupMeta: Map<
      string,
      {
        count: number;
        sourceModifiedEpochMs?: number;
        lastSyncEpochMs?: number;
        lastStatus?: 'completed' | 'failed' | 'skipped';
      }
    >;
  } {
    const key = '__LOOKUP_SERVICE_STATE__';
    if (!(globalThis as any)[key]) {
      (globalThis as any)[key] = {
        initialized: false,
        initFailed: false,
        retryCount: 0,
        pendingRequests: new Map<string, Promise<any[]>>(),
        baseUrl: '',
        memoryCache: new Map<string, any[]>(),
        drugCodesSearchCache: new Map<string, any[]>(),
        deferredLookupMeta: new Map(),
      };
    }
    return (globalThis as any)[key];
  }

  /** Get effective base URL - uses injected value or global state */
  private get baseUrl(): string {
    // If this instance has a URL, store it globally for other MFE instances
    if (this.injectedBaseUrl) {
      LookupService.global.baseUrl = this.injectedBaseUrl;
      return this.injectedBaseUrl;
    }
    // Fall back to globally stored URL (set by shell or another MFE)
    return LookupService.global.baseUrl;
  }

  // ===========================================================================
  // Initialization
  // ===========================================================================

  /** Initialize service - sets up IndexedDB (no API call needed, uses schema-registry) */
  async init(): Promise<void> {
    const g = LookupService.global;

    // Already initialized successfully
    if (g.initialized) {
      return;
    }

    // Failed after max retries - don't keep trying
    if (g.initFailed) {
      return;
    }

    // No base URL configured
    if (!this.baseUrl) {
      console.warn('[LookupService] No LOOKUP_API_BASE_URL provided');
      g.initFailed = true;
      return;
    }

    // Initialize IndexedDB store (only if enabled)
    try {
      if (this.store.isEnabled()) {
        await this.store.initialize();
      }

      g.initialized = true;
      g.retryCount = 0;
    } catch (error) {
      g.retryCount++;
      console.error(`[LookupService] Init failed (attempt ${g.retryCount}/${MAX_RETRIES}):`, error);

      if (g.retryCount >= MAX_RETRIES) {
        console.error('[LookupService] Max retries reached. Lookup features disabled.');
        g.initFailed = true;
      }
    }
  }

  // ===========================================================================
  // Public API
  // ===========================================================================

  /** Get all lookup data (creates table on first call) */
  async get(lookupName: string, queryParams?: Record<string, string | number>): Promise<any[]> {
    return this.ensureDataAndReturn(lookupName, () => this.store.getAll(lookupName), queryParams);
  }

  /**
   * Resolves only the requested ICD metadata through the configured API or cached lookup strategy. This keeps
   * Assessment & Plan eligibility validation aligned with online and offline-ready ICD searches.
   */
  async getIcdCodeMetadata(codes: string[], isOfflineReady = false): Promise<Map<string, IcdCodeMetadata>> {
    const result = await this.resolveIcdCodeMetadata(codes, isOfflineReady);
    return result.metadataByCode;
  }

  async resolveIcdCodeMetadata(
    codes: string[],
    isOfflineReady = false
  ): Promise<{ metadataByCode: Map<string, IcdCodeMetadata>; lookupSucceeded: boolean }> {
    const normalizedCodes = [...new Set(codes.map((code) => code.replace(/\s+/g, '').toUpperCase()).filter(Boolean))];
    if (normalizedCodes.length === 0) return { metadataByCode: new Map(), lookupSucceeded: true };

    let lookupSucceeded = true;
    const canUseApi = !isOfflineReady && (typeof navigator === 'undefined' || navigator.onLine);
    if (this.shouldUseApiSearch('icd-codes', isOfflineReady) || (!this.store.isEnabled() && canUseApi)) {
      try {
        if (!this.baseUrl) {
          throw new Error('[LookupService] No API base URL available. Cannot resolve ICD metadata.');
        }
        const rows = await firstValueFrom(
          this.http.post<Record<string, unknown>[]>(`${this.baseUrl}/api/Lookup/icd-codes/metadata`, normalizedCodes, {
            headers: new HttpHeaders({ 'X-Skip-Loading': 'true' }),
          })
        );
        return { metadataByCode: this.toIcdCodeMetadataMap(rows ?? [], normalizedCodes), lookupSucceeded: true };
      } catch (error) {
        if (!this.store.isEnabled()) throw error;
        lookupSucceeded = false;
        console.warn('[LookupService] ICD metadata API failed. Falling back to cached lookup.', error);
      }
    }

    if (!this.store.isEnabled()) {
      throw new Error('[LookupService] ICD code cache is unavailable.');
    }

    await this.init();
    if (!this.store.isReady() || !this.store.tableExists('icd-codes')) {
      throw new Error('[LookupService] ICD code cache is unavailable.');
    }
    const rows = await this.store.searchByAnyEquals<Record<string, unknown>>(
      'icd-codes',
      'code',
      normalizedCodes,
      true
    );
    return { metadataByCode: this.toIcdCodeMetadataMap(rows, normalizedCodes), lookupSucceeded };
  }

  /** Resolve many exact ICD codes with their display descriptions without scanning the cached table. */
  async getIcdCodesByCodes(
    codes: string[],
    options: { apiOnly?: boolean; isShow?: boolean } = {}
  ): Promise<Map<string, { code: string; description: string }>> {
    const requested = [...new Set(codes.map((code) => code.trim().toUpperCase()).filter(Boolean))];
    const matches = new Map<string, { code: string; description: string }>();
    if (requested.length === 0) {
      return matches;
    }

    const requestedByCode = new Map(requested.map((code) => [code.replace(/\./g, ''), code]));
    const addRows = (rows: Record<string, unknown>[]): void => {
      for (const row of rows) {
        if (options.isShow === true && (row.isShow ?? row.IsShow) === false) continue;
        const rawCode = String(row.code ?? row.Code ?? '')
          .trim()
          .toUpperCase();
        const code = options.apiOnly ? requestedByCode.get(rawCode.replace(/\./g, '')) : rawCode;
        if (code) {
          matches.set(code, {
            code,
            description: String(row.icdDescription ?? row.IcdDescription ?? row.description ?? row.Description ?? ''),
          });
        }
      }
    };

    if (options.apiOnly) {
      if (!this.baseUrl) throw new Error('[LookupService] No API base URL available for live ICD lookup.');
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        throw new Error('[LookupService] Live ICD lookup requires an online connection.');
      }
      const rows = await firstValueFrom(
        this.http.post<Record<string, unknown>[]>(`${this.baseUrl}/api/Lookup/icd-codes/metadata`, requested, {
          headers: new HttpHeaders({ 'X-Skip-Loading': 'true' }),
        })
      );
      addRows(rows ?? []);
      return matches;
    }

    if (!this.store.isEnabled()) {
      const requestedSet = new Set(requested);
      const rows = await this.fetchFromAPIWithoutParams('icd-codes');
      addRows(
        rows.filter((row) =>
          requestedSet.has(
            String(row.code ?? row.Code ?? '')
              .trim()
              .toUpperCase()
          )
        )
      );
      return matches;
    }

    await this.ensureDataAndReturn('icd-codes', async () => []);
    if (!this.store.isReady() || !this.store.tableExists('icd-codes')) {
      throw new Error('[LookupService] ICD code cache is unavailable.');
    }
    for (let index = 0; index < requested.length; index += 250) {
      addRows(
        await this.store.searchByAnyEquals<Record<string, unknown>>(
          'icd-codes',
          'code',
          requested.slice(index, index + 250)
        )
      );
    }
    return matches;
  }

  private toIcdCodeMetadataMap(rows: any[], normalizedCodes: string[]): Map<string, IcdCodeMetadata> {
    const metadata = new Map<string, IcdCodeMetadata>();
    for (const row of rows) {
      const code = String(row.code ?? row.Code ?? '')
        .replace(/\s+/g, '')
        .toUpperCase();
      if (!code || !normalizedCodes.includes(code) || metadata.has(code)) continue;
      metadata.set(code, {
        code,
        isShow: row.isShow ?? row.IsShow,
        effectiveDate: row.effectiveDate ?? row.EffectiveDate,
        termDate: row.termDate ?? row.TermDate,
      });
    }
    return metadata;
  }

  /** Paged search with offset support for incremental-loading UIs. */
  async searchPage(request: LookupSearchPageRequest): Promise<LookupSearchPageResponse<any>> {
    const lookupName = normalizeLookupName(request.lookupName);
    const query = request.query ?? '';
    const pageSize = Math.max(1, request.pageSize ?? 10);
    const offset = Math.max(0, request.offset ?? 0);
    const queryParams = request.queryParams;
    const filterContext = request.filterContext;

    if (request.apiOnly) {
      if (request.isOfflineReady)
        throw new Error('[LookupService] API-only search cannot use an offline-ready record.');
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        throw new Error('[LookupService] API-only search requires an online connection.');
      }
      return this.searchPageFromApi(lookupName, query.trim(), pageSize, offset, filterContext);
    }

    if (lookupName === 'loinc-codes' && query.trim()) {
      return this.searchLoincCodesPage(query, pageSize, offset);
    }

    if (this.shouldUseApiSearch(lookupName, request.isOfflineReady)) {
      try {
        return await this.searchPageFromApi(lookupName, query, pageSize, offset, filterContext);
      } catch (error) {
        console.warn(`[LookupService] API search failed for "${lookupName}". Falling back to local search.`, error);
      }
    }

    if (this.isLiveApiOnlyLookup(lookupName)) {
      if (!this.baseUrl) {
        throw new Error(`[LookupService] No API base URL available. Cannot search lookup "${lookupName}".`);
      }
      const data = queryParams
        ? await this.fetchFromAPI(lookupName, queryParams)
        : await this.fetchFromAPIFresh(lookupName);
      const customFiltered = this.applyCustomFilters(lookupName, data, filterContext);
      const filtered = this.filterDataClientSide(customFiltered, query, Number.MAX_SAFE_INTEGER);
      const items = filtered.slice(offset, offset + pageSize);
      const result = {
        items,
        hasMore: offset + items.length < filtered.length,
        nextOffset: offset + items.length < filtered.length ? offset + items.length : null,
      };
      return result;
    }

    // IndexedDB disabled - API + client-side page window
    if (!this.store.isEnabled()) {
      if (!this.baseUrl) {
        throw new Error(`[LookupService] No API base URL available. Cannot search lookup "${lookupName}".`);
      }
      const rawData =
        queryParams && Object.keys(queryParams).length > 0
          ? await this.fetchFromAPI(lookupName, queryParams)
          : await this.fetchFromAPIWithoutParams(lookupName);

      const customFiltered = this.applyCustomFilters(lookupName, rawData, filterContext);
      const filtered = this.filterDataClientSide(customFiltered, query, Number.MAX_SAFE_INTEGER);
      const items = filtered.slice(offset, offset + pageSize);
      const result = {
        items,
        hasMore: offset + items.length < filtered.length,
        nextOffset: offset + items.length < filtered.length ? offset + items.length : null,
      };
      return result;
    }

    // Query-param path currently needs API fetch, then filtered/paged locally.
    if (queryParams && Object.keys(queryParams).length > 0) {
      const searchData = await this.fetchFromAPI(lookupName, queryParams);
      const customFiltered = this.applyCustomFilters(lookupName, searchData, filterContext);
      const filtered = this.filterDataClientSide(customFiltered, query, Number.MAX_SAFE_INTEGER);
      const items = filtered.slice(offset, offset + pageSize);
      const result = {
        items,
        hasMore: offset + items.length < filtered.length,
        nextOffset: offset + items.length < filtered.length ? offset + items.length : null,
      };
      return result;
    }

    // For large/static lookups, prefer IndexedDB rows once available
    // and avoid repeated freshness checks that can trigger network reloads.
    if (this.isIndexedDbFirstLookup(lookupName)) {
      await this.init();

      // If IndexedDB is ready, use cached rows directly.
      if (this.store.isReady()) {
        if (this.store.tableExists(lookupName)) {
          const count = await this.store.count(lookupName);
          if (count > 0) {
            return this.queryCachedLookupPage(lookupName, query, pageSize, offset, filterContext);
          }
        }

        // Table missing/empty: load once from API, then query cached rows.
        const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
        if (isOnline) {
          await this.createTableAndLoadData(lookupName);
          return this.queryCachedLookupPage(lookupName, query, pageSize, offset, filterContext);
        }
      }
    }

    return this.ensureDataAndReturn(lookupName, async () => {
      return this.queryCachedLookupPage(lookupName, query, pageSize, offset, filterContext);
    });
  }

  private shouldUseApiSearch(lookupName: string, isOfflineReady?: boolean): boolean {
    // The caller (assessment/appointment context) knows whether the active record was downloaded for
    // offline use. When it was, always search the cached IndexedDB/Web Worker copy so results stay
    // consistent with what was downloaded — regardless of whether connectivity happens to be up.
    if (isOfflineReady) return false;

    const isOnline = typeof navigator === 'undefined' || navigator.onLine;
    if (!isOnline) return false;

    const tableMode = this.getLookupSearchConfig()?.tableModes?.[lookupName];
    if (!tableMode) return false;

    return this.getLookupSearchMode(tableMode) === 'api';
  }

  private getLookupSearchConfig(): LookupSearchConfig | undefined {
    if (this.lookupSearchConfig) return this.lookupSearchConfig;
    return (globalThis as typeof globalThis & { __SYMPHONY_LOOKUP_SEARCH_CONFIG__?: LookupSearchConfig })
      .__SYMPHONY_LOOKUP_SEARCH_CONFIG__;
  }

  private getLookupSearchMode(tableMode: LookupSearchMode): LookupSearchMode {
    if (typeof localStorage !== 'undefined') {
      const override = localStorage.getItem(LOOKUP_SEARCH_MODE_OVERRIDE_STORAGE_KEY);
      if (override === 'api' || override === 'worker') return override;
    }

    return tableMode;
  }

  private async searchPageFromApi(
    lookupName: string,
    query: string,
    pageSize: number,
    offset: number,
    filterContext?: LookupSearchFilter
  ): Promise<LookupSearchPageResponse<any>> {
    if (!this.baseUrl) {
      throw new Error(`[LookupService] No API base URL available. Cannot search lookup "${lookupName}".`);
    }

    let params = new HttpParams().set('query', query).set('pageSize', pageSize).set('offset', offset);
    if (lookupName === 'icd-codes') {
      const origin = filterContext?.origin?.toLowerCase();
      if (filterContext?.lobName?.trim() && shouldFilterIcdByLob(filterContext)) {
        params = params.set('lobName', filterContext.lobName.trim());
      }
      if (filterContext?.isShow === true || origin === 'stage' || origin === 'orchestra')
        params = params.set('isShow', 'true');
      if (filterContext?.dateToFilter) {
        params = params.set('activeOnDate', this.toLookupSearchDate(filterContext.dateToFilter));
      }
      if (filterContext?.rowStatus === 'ACTIVE_TODAY' && filterContext.hccCode != null) {
        params = params.set('hccCode', String(filterContext.hccCode));
      }
    }

    const url = `${this.baseUrl}/api/Lookup/${lookupName}/search`;
    const response = await firstValueFrom(
      this.http.get<{ items?: any[]; hasMore?: boolean; nextOffset?: number }>(url, {
        params,
        headers: new HttpHeaders({ 'X-Skip-Loading': 'true' }),
      })
    );
    const hasMore = response?.hasMore === true;
    return {
      items:
        lookupName === 'icd-codes' && params.has('isShow')
          ? (response?.items ?? []).filter((row) => (row.isShow ?? row.IsShow) !== false)
          : (response?.items ?? []),
      hasMore,
      nextOffset: hasMore ? (response?.nextOffset ?? offset + pageSize) : null,
    };
  }

  private async searchLoincCodesPage(
    query: string,
    pageSize: number,
    offset: number
  ): Promise<LookupSearchPageResponse<any>> {
    if (!this.baseUrl) {
      throw new Error('[LookupService] No API base URL available. Cannot search lookup "loinc-codes".');
    }

    const response = await firstValueFrom(
      this.http.get<{ items?: any[]; hasMore?: boolean; nextOffset?: number }>(
        `${this.baseUrl}/api/Lookup/loinc-codes/loinc-search`,
        {
          params: new HttpParams().set('query', query).set('pageSize', pageSize).set('offset', offset),
          headers: new HttpHeaders({ 'X-Skip-Loading': 'true' }),
        }
      )
    );
    const hasMore = response?.hasMore === true;
    return {
      items: this.normalizeLookupRows('loinc-codes', response?.items ?? []),
      hasMore,
      nextOffset: hasMore ? (response?.nextOffset ?? offset + pageSize) : null,
    };
  }

  private toLookupSearchDate(value: Date | string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toISOString().slice(0, 10);
  }

  /**
   * Searches ICD codes by a single case-insensitive substring in either the code or description. This is intended for
   * clinical rule configuration, where every returned record must contain the full query.
   */
  async searchIcdCodesBySubstringPage(
    request: Omit<LookupSearchPageRequest, 'lookupName'>
  ): Promise<LookupSearchPageResponse<any>> {
    const query = request.query ?? '';
    const pageSize = Math.max(1, request.pageSize ?? 10);
    const offset = Math.max(0, request.offset ?? 0);
    const searchFields = ['code', 'Code', 'icdDescription', 'IcdDescription', 'description', 'Description'];
    const toPage = (items: any[]): LookupSearchPageResponse<any> => {
      const pageWindow = items.slice(offset, offset + pageSize + 1);
      const hasMore = pageWindow.length > pageSize;
      const pageItems = hasMore ? pageWindow.slice(0, pageSize) : pageWindow;
      return {
        items: pageItems,
        hasMore,
        nextOffset: hasMore ? offset + pageItems.length : null,
      };
    };
    const matchesQuery = (item: Record<string, unknown>) => matchesSubstringSearch(item, query, searchFields);

    if (!this.store.isEnabled()) {
      if (!this.baseUrl) {
        throw new Error('[LookupService] No API base URL available. Cannot search lookup "icd-codes".');
      }
      const data = await this.fetchFromAPIWithoutParams('icd-codes');
      return toPage(data.filter(matchesQuery));
    }

    return this.ensureDataAndReturn('icd-codes', async () => {
      const items = await this.store.searchWithPredicatePage('icd-codes', matchesQuery, pageSize + 1, offset);
      const hasMore = items.length > pageSize;
      const pageItems = hasMore ? items.slice(0, pageSize) : items;
      return {
        items: pageItems,
        hasMore,
        nextOffset: hasMore ? offset + pageItems.length : null,
      };
    });
  }

  private async queryCachedLookupPage(
    lookupName: string,
    query: string,
    pageSize: number,
    offset: number,
    filterContext?: LookupSearchFilter
  ): Promise<LookupSearchPageResponse<any>> {
    const g = LookupService.global;
    const schema = getLookupTableSchema(lookupName);
    const lowerQuery = query?.trim().toLowerCase();
    const indexes = schema?.indexes || [];

    const filterOrigin = filterContext?.origin?.toLowerCase();
    const rowStatus = filterContext?.rowStatus;
    const requestedHcc = filterContext?.hccCode;
    const requestedLob = shouldFilterIcdByLob(filterContext) ? filterContext?.lobName : undefined;

    // Parse date filter once
    let filterDate: Date | null = null;
    if (filterContext?.dateToFilter) {
      filterDate = new Date(filterContext.dateToFilter);
      filterDate.setHours(0, 0, 0, 0);
    }

    const hasCustomFilters = Boolean(filterContext && lookupName === 'icd-codes');

    const workerSearchConfig = this.getWorkerSearchConfig(lookupName, schema?.primaryKey, filterContext);
    if (workerSearchConfig) {
      const workerPage = await this.lookupSearchCoordinator.searchPage(workerSearchConfig, query, pageSize, offset);
      if (workerPage === LOOKUP_SEARCH_SUPERSEDED) {
        return { items: [], hasMore: false, nextOffset: null };
      }
      if (workerPage) {
        return workerPage;
      }
    }

    // ========================================================================
    // STAGE 1: IndexedDB search
    // ========================================================================

    // Fast path: drug-codes search using indexed prefix candidates.
    if (lookupName === 'drug-codes') {
      if (!lowerQuery) {
        return {
          items: [],
          hasMore: false,
          nextOffset: null,
        };
      }

      const cacheKey = lowerQuery;
      const cached = g.drugCodesSearchCache.get(cacheKey);
      if (cached) {
        const cachedItems = cached.slice(offset, offset + pageSize);
        const cachedHasMore = cached.length > offset + pageSize;
        return {
          items: cachedItems,
          hasMore: cachedHasMore,
          nextOffset: cachedHasMore ? offset + cachedItems.length : null,
        };
      }

      const terms = lowerQuery
        .split(/\s+/)
        .map((t) => t.trim())
        .filter(Boolean);

      const primaryTerm = terms[0];
      if (!primaryTerm) {
        return {
          items: [],
          hasMore: false,
          nextOffset: null,
        };
      }

      const candidateLimit = Math.max(pageSize * 8, 200);
      const candidates = await this.store.searchByPrefix(lookupName, 'medicationName', primaryTerm, candidateLimit);

      const deduped = new Map<string, any>();
      candidates.forEach((item: any) => {
        const key = String(item.medicationName ?? item.MedicationName ?? '')
          .trim()
          .toLowerCase();
        if (key && !deduped.has(key)) {
          deduped.set(key, item);
        }
      });

      const workerConfig = WORKER_SEARCH_TABLES[lookupName]!;
      let filtered = Array.from(deduped.values()).filter((item: any) =>
        matchesLookupSearch(item, query, workerConfig.searchFields)
      );

      // Fallback path (ICD-like resilient behavior): if indexed prefix scan yields no matches,
      // perform a bounded predicate search to handle schema/key mismatches without freezing UI.
      if (filtered.length === 0) {
        const fallbackLimit = Math.max(pageSize * 8, 200);
        filtered = await this.store.searchWithPredicate(
          lookupName,
          (item: any) => matchesLookupSearch(item, query, workerConfig.searchFields),
          fallbackLimit
        );
      }

      g.drugCodesSearchCache.set(cacheKey, filtered);

      const items = filtered.slice(offset, offset + pageSize);
      const hasMore = filtered.length > offset + pageSize;
      return {
        items,
        hasMore,
        nextOffset: hasMore ? offset + items.length : null,
      };
    }

    const terms = lowerQuery
      ? lowerQuery
          .split(/\s+/)
          .map((term) => term.trim())
          .filter(Boolean)
      : [];
    const searchableFields = [
      ...indexes,
      'code',
      'icdDescription',
      'description',
      'cptDescription',
      'cpt2Description',
      'modifierDescription',
      'name',
      'displayText',
    ];
    const requestedLobLower = requestedLob?.trim().toLowerCase();

    const matchedRows = await this.store.searchWithPredicatePage(
      lookupName,
      (item: any) => {
        if (hasCustomFilters) {
          if (requestedLobLower) {
            const itemLob = item.lobName ?? item.LobName;
            const matchesLob = !itemLob || String(itemLob).trim().toLowerCase() === requestedLobLower;
            if (!matchesLob) {
              return false;
            }
          }

          if (filterContext?.isShow === true || filterOrigin === 'stage' || filterOrigin === 'orchestra') {
            const isVisible = (item.isShow ?? item.IsShow) !== false;
            if (!isVisible) {
              return false;
            }
          }

          if (filterDate) {
            const rawTermDate = item.termDate ?? item.TermDate;
            const rawEffectiveDate = item.effectiveDate ?? item.EffectiveDate;
            if (rawTermDate != null) {
              const termDate = new Date(rawTermDate);
              termDate.setHours(0, 0, 0, 0);
              if (!isNaN(termDate.getTime()) && termDate <= filterDate) {
                return false;
              }
            }

            if (rawEffectiveDate != null) {
              const effectiveDate = new Date(rawEffectiveDate);
              effectiveDate.setHours(0, 0, 0, 0);
              if (!isNaN(effectiveDate.getTime()) && effectiveDate > filterDate) {
                return false;
              }
            }
          }

          if (rowStatus === 'ACTIVE_TODAY' && requestedHcc != null) {
            const matchesHcc = this.hccMatches(item.hccCode ?? item.HccCode, requestedHcc);
            if (!matchesHcc) {
              return false;
            }
          }
        }

        if (terms.length === 0) return true;
        const haystack = searchableFields
          .map((column) => String(item[column] ?? ''))
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return terms.every((term) => haystack.includes(term));
      },
      pageSize + 1,
      offset
    );

    const hasMore = matchedRows.length > pageSize;
    const items = hasMore ? matchedRows.slice(0, pageSize) : matchedRows;
    return {
      items,
      hasMore,
      nextOffset: hasMore ? offset + items.length : null,
    };
  }

  /**
   * Search lookup data using substring matching on all indexed columns.
   *
   * @param lookupName - Lookup endpoint name
   * @param query - Search text (case-insensitive substring)
   * @param limit - Max results (default: 10)
   * @param queryParams - Optional query parameters for API filtering
   */
  async search(
    lookupName: string,
    query: string,
    limit = 10,
    queryParams?: Record<string, string | number>,
    filterContext?: LookupSearchFilter,
    isOfflineReady?: boolean,
    apiOnly = false
  ): Promise<any[]> {
    const page = await this.searchPage({
      lookupName,
      query,
      pageSize: limit,
      offset: 0,
      queryParams,
      filterContext,
      isOfflineReady,
      apiOnly,
    });
    return page.items;
  }

  /** Applies custom business rule filters (like ICD term/effective dates) based on the filter context */
  private applyCustomFilters(lookupName: string, data: any[], filterContext?: LookupSearchFilter): any[] {
    if (!filterContext || lookupName !== 'icd-codes') return data;

    let filterDate: Date | null = null;
    if (filterContext.dateToFilter) {
      filterDate = new Date(filterContext.dateToFilter);
      filterDate.setHours(0, 0, 0, 0); // Normalize to start of day for comparison if needed
    }

    return data.filter((item) => {
      if (filterDate) {
        const termDateRaw = item.termDate ?? item.TermDate;
        if (termDateRaw) {
          const termDate = new Date(termDateRaw);
          termDate.setHours(0, 0, 0, 0);
          if (termDate < filterDate) return false;
        }

        const effectiveDateRaw = item.effectiveDate ?? item.EffectiveDate;
        if (effectiveDateRaw) {
          const effectiveDate = new Date(effectiveDateRaw);
          effectiveDate.setHours(0, 0, 0, 0);
          if (effectiveDate > filterDate) return false;
        }
      }

      const origin = filterContext.origin?.toLowerCase();
      if (filterContext.isShow === true || origin === 'stage' || origin === 'orchestra') {
        const isShow = item.isShow ?? item.IsShow;
        if (isShow === false) return false;
      }

      if (shouldFilterIcdByLob(filterContext) && filterContext.lobName) {
        const lobName = item.lobName ?? item.LobName;
        if (lobName && String(lobName) !== String(filterContext.lobName)) return false;
      }

      if (filterContext?.rowStatus === 'ACTIVE_TODAY' && filterContext.hccCode != null) {
        const itemHcc = item.hccCode ?? item.HccCode;
        if (!this.hccMatches(itemHcc, filterContext.hccCode)) return false;
      }

      return true;
    });
  }

  /**
   * Client-side filtering for search when IndexedDB is disabled. Searches across all string fields since we don't have
   * config.indexes available.
   */
  private filterDataClientSide(data: any[], query: string, limit: number): any[] {
    if (!data?.length) return [];

    const lowerQuery = query?.trim().toLowerCase();
    if (!lowerQuery) {
      return data.slice(0, limit);
    }

    // Search across all string fields (code, description, etc.)
    return data
      .filter((item: any) =>
        Object.values(item).some((val) => typeof val === 'string' && val.toLowerCase().includes(lowerQuery))
      )
      .slice(0, limit);
  }

  /**
   * Search by exact code match (FAST - uses index).
   *
   * @param lookupName - Lookup endpoint name
   * @param code - Exact code to find
   */
  async searchByCode(lookupName: string, code: string): Promise<any[]> {
    lookupName = normalizeLookupName(lookupName);

    if (this.isLiveApiOnlyLookup(lookupName)) {
      if (!this.baseUrl) {
        throw new Error(`[LookupService] No API base URL available.`);
      }
      const allData = await this.fetchFromAPIFresh(lookupName);
      const lowerCode = code.toLowerCase();
      return allData.filter((item: any) =>
        Object.entries(item).some(
          ([key, val]) =>
            key.toLowerCase().includes('code') && typeof val === 'string' && val.toLowerCase() === lowerCode
        )
      );
    }

    // IndexedDB disabled - fetch and filter client-side
    if (!this.store.isEnabled()) {
      if (!this.baseUrl) {
        throw new Error(`[LookupService] No API base URL available.`);
      }
      const allData = await this.fetchFromAPIWithoutParams(lookupName);
      const lowerCode = code.toLowerCase();
      return allData.filter((item: any) =>
        Object.entries(item).some(
          ([key, val]) =>
            key.toLowerCase().includes('code') && typeof val === 'string' && val.toLowerCase() === lowerCode
        )
      );
    }

    return this.ensureDataAndReturn(lookupName, async () => {
      const schema = getLookupTableSchema(lookupName);
      const codeColumn = schema?.indexes?.find((c) => c.toLowerCase().includes('code'));

      if (!codeColumn) {
        throw new Error(`[LookupService] No code column found for ${lookupName}`);
      }

      return this.store.searchByEquals(lookupName, codeColumn, code);
    });
  }

  /**
   * Search by prefix on code column (FAST - uses index).
   *
   * @param lookupName - Lookup endpoint name
   * @param prefix - Code prefix (e.g., "A00" finds "A00.1", "A00.9")
   * @param limit - Max results (default: 10)
   */
  async searchByCodePrefix(lookupName: string, prefix: string, limit = 10): Promise<any[]> {
    lookupName = normalizeLookupName(lookupName);

    if (this.isLiveApiOnlyLookup(lookupName)) {
      if (!this.baseUrl) {
        throw new Error(`[LookupService] No API base URL available.`);
      }
      const allData = await this.fetchFromAPIFresh(lookupName);
      const lowerPrefix = prefix.toLowerCase();
      return allData
        .filter((item: any) =>
          Object.entries(item).some(
            ([key, val]) =>
              key.toLowerCase().includes('code') && typeof val === 'string' && val.toLowerCase().startsWith(lowerPrefix)
          )
        )
        .slice(0, limit);
    }

    // IndexedDB disabled - fetch and filter client-side
    if (!this.store.isEnabled()) {
      if (!this.baseUrl) {
        throw new Error(`[LookupService] No API base URL available.`);
      }
      const allData = await this.fetchFromAPIWithoutParams(lookupName);
      const lowerPrefix = prefix.toLowerCase();
      return allData
        .filter((item: any) =>
          Object.entries(item).some(
            ([key, val]) =>
              key.toLowerCase().includes('code') && typeof val === 'string' && val.toLowerCase().startsWith(lowerPrefix)
          )
        )
        .slice(0, limit);
    }

    return this.ensureDataAndReturn(lookupName, async () => {
      const schema = getLookupTableSchema(lookupName);
      const codeColumn = schema?.indexes?.find((c) => c.toLowerCase().includes('code'));

      if (!codeColumn) {
        throw new Error(`[LookupService] No code column found for ${lookupName}`);
      }

      return this.store.searchByPrefix(lookupName, codeColumn, prefix, limit);
    });
  }

  /**
   * Search description with substring (slower but flexible).
   *
   * @param lookupName - Lookup endpoint name
   * @param term - Text to find in description
   * @param limit - Max results (default: 10)
   */
  async searchByDescription(lookupName: string, term: string, limit = 10): Promise<any[]> {
    lookupName = normalizeLookupName(lookupName);

    if (this.isLiveApiOnlyLookup(lookupName)) {
      if (!this.baseUrl) {
        throw new Error(`[LookupService] No API base URL available.`);
      }
      const allData = await this.fetchFromAPIFresh(lookupName);
      const lowerTerm = term.toLowerCase();
      return allData
        .filter((item: any) =>
          Object.entries(item).some(
            ([key, val]) =>
              key.toLowerCase().includes('description') &&
              typeof val === 'string' &&
              val.toLowerCase().includes(lowerTerm)
          )
        )
        .slice(0, limit);
    }

    // IndexedDB disabled - fetch and filter client-side
    if (!this.store.isEnabled()) {
      if (!this.baseUrl) {
        throw new Error(`[LookupService] No API base URL available.`);
      }
      const allData = await this.fetchFromAPIWithoutParams(lookupName);
      const lowerTerm = term.toLowerCase();
      return allData
        .filter((item: any) =>
          Object.entries(item).some(
            ([key, val]) =>
              key.toLowerCase().includes('description') &&
              typeof val === 'string' &&
              val.toLowerCase().includes(lowerTerm)
          )
        )
        .slice(0, limit);
    }

    return this.ensureDataAndReturn(lookupName, async () => {
      const schema = getLookupTableSchema(lookupName);
      const descColumns = schema?.indexes?.filter((c) => c.toLowerCase().includes('description')) || [];

      if (descColumns.length === 0) {
        throw new Error(`[LookupService] No description column found for ${lookupName}`);
      }

      return this.store.searchBySubstring(lookupName, descColumns, term, limit);
    });
  }

  /** Get single item by primary key */
  async getByKey(lookupName: string, key: any): Promise<any | null> {
    lookupName = normalizeLookupName(lookupName);

    if (this.isLiveApiOnlyLookup(lookupName)) {
      if (!this.baseUrl) {
        throw new Error(`[LookupService] No API base URL available.`);
      }
      const allData = await this.fetchFromAPIFresh(lookupName);
      return (
        allData.find((item: any) =>
          Object.entries(item).some(([k, v]) => k.toLowerCase().endsWith('id') && v === key)
        ) ?? null
      );
    }

    // IndexedDB disabled - fetch and find client-side
    if (!this.store.isEnabled()) {
      if (!this.baseUrl) {
        throw new Error(`[LookupService] No API base URL available.`);
      }
      const allData = await this.fetchFromAPIWithoutParams(lookupName);
      // Find by any field ending with 'id' that matches the key
      return (
        allData.find((item: any) =>
          Object.entries(item).some(([k, v]) => k.toLowerCase().endsWith('id') && v === key)
        ) ?? null
      );
    }

    return this.ensureDataAndReturn(lookupName, () => this.store.getByKey(lookupName, key));
  }

  /** Get display field for a lookup */
  async getDisplayField(lookupName: string): Promise<string> {
    // Schema-registry is in-memory — no IndexedDB or init() needed.
    // Always check it first so lookups like 'clients' → 'clientName' are resolved
    // correctly even when IndexedDB is disabled or failed to initialise.
    const schema = getLookupTableSchema(lookupName);
    return schema?.indexes?.[0] ?? 'description';
  }

  /** Check if lookup is supported (registered in LOOKUP_TABLES) */
  async isSupported(lookupName: string): Promise<boolean> {
    return this.isSupportedLookup(lookupName);
  }

  /** Get config for a lookup (built from schema-registry) */
  async getConfig(lookupName: string): Promise<LookupConfig | undefined> {
    const canonical = normalizeLookupName(lookupName);
    const schema = getLookupTableSchema(canonical);
    if (!schema) {
      if (!this.isLiveApiOnlyLookup(canonical)) {
        return undefined;
      }

      return {
        lookupConfigId: 0,
        lookupName: canonical,
        endpoint: canonical,
        labelColumn: 'description',
        isActive: true,
      };
    }
    return {
      lookupConfigId: 0,
      lookupName: canonical,
      endpoint: canonical,
      labelColumn: schema.indexes?.[0] ?? 'description',
      isActive: true,
      primaryKey: schema.primaryKey,
      indexes: schema.indexes,
    };
  }

  /** Get all available lookup names (from LOOKUP_TABLES) */
  async getAvailableLookups(): Promise<string[]> {
    const available = new Set(getSchemaLookupTableNames());
    LIVE_API_ONLY_LOOKUPS.forEach((name) => available.add(name));
    return Array.from(available);
  }

  /** Get only pre-registered lookup table names from schema-registry. */
  getRegisteredLookupTableNames(): string[] {
    return [...getSchemaLookupTableNames()];
  }

  /**
   * Snapshot local lookup metadata for SW-side prefetch decisions. Includes count/source-modified only for requested
   * registered tables.
   *
   * Reads only the `_meta` table (one bulk get) — never `count()` per data table, which would open every large lookup
   * store and stall the "checking" phase on module navigation.
   */
  async getLookupSyncMetaSnapshot(tableNames: string[]): Promise<
    Record<
      string,
      {
        count: number;
        sourceModifiedEpochMs?: number;
        lastSyncEpochMs?: number;
      }
    >
  > {
    const snapshot: Record<
      string,
      {
        count: number;
        sourceModifiedEpochMs?: number;
        lastSyncEpochMs?: number;
      }
    > = {};

    if (!this.store.isEnabled()) {
      return snapshot;
    }

    await this.init();
    if (!this.store.isReady()) {
      return snapshot;
    }

    const uniqueTables = Array.from(new Set(tableNames.map((name) => normalizeLookupName(name)))).filter(
      (tableName) => isRegisteredLookupTable(tableName) && this.store.tableExists(tableName)
    );

    const metaRows = await this.store.getMetaBatch(uniqueTables);

    uniqueTables.forEach((tableName, index) => {
      const meta = metaRows[index];
      if (!meta) return;

      snapshot[tableName] = {
        count: meta.count ?? 0,
        sourceModifiedEpochMs: meta.sourceModifiedEpochMs,
        lastSyncEpochMs: meta.lastSyncEpochMs,
      };
    });

    return snapshot;
  }

  /**
   * True while a completed sync is still on record. The marker lives in the same store as the lookup rows, so a browser
   * "Clear site data" or storage eviction takes it with them and callers can tell the cache was wiped. Table-by-table
   * coverage is deliberately not used: the registry lists more tables than the server returns.
   */
  async hasCompleteLookupCache(): Promise<boolean> {
    // With IndexedDB off there is no cache to keep populated, so report "nothing to do".
    if (!this.store.isEnabled()) {
      return true;
    }

    await this.init();
    if (!this.store.isReady()) {
      return false;
    }

    return Boolean(await this.store.getMeta(LOOKUP_SYNC_COMPLETE_MARKER));
  }

  /** Gets the active IndexedDB table set reported by lookup-configs during the last full preload. */
  async getFullPreloadLookupTableNames(): Promise<string[]> {
    if (!this.store.isEnabled()) {
      return [];
    }

    await this.init();
    if (!this.store.isReady()) {
      return [];
    }

    const meta = await this.store.getMeta(LOOKUP_SYNC_COMPLETE_MARKER);
    return Array.isArray(meta?.lookupConfigTableNames)
      ? meta.lookupConfigTableNames
          .map((tableName) => normalizeLookupName(tableName))
          .filter((tableName) => isRegisteredLookupTable(tableName))
      : [];
  }

  /** Records that a sync finished so a later navigation can tell a wiped cache from a merely partial one. */
  async markLookupCacheComplete(tableCount: number, lookupConfigTableNames?: string[]): Promise<void> {
    if (!this.store.isEnabled()) {
      return;
    }

    await this.init();
    if (!this.store.isReady()) {
      return;
    }

    const existingMeta = await this.store.getMeta(LOOKUP_SYNC_COMPLETE_MARKER);
    const tableNames = lookupConfigTableNames ?? existingMeta?.lookupConfigTableNames;
    await this.store.setMeta(LOOKUP_SYNC_COMPLETE_MARKER, tableCount, {
      lookupConfigTableNames: tableNames,
    });
  }

  /** Records that existing lookup tables were confirmed unchanged by a successful config check. */
  async markLookupTablesChecked(tableNames: string[]): Promise<void> {
    if (!this.store.isEnabled() || tableNames.length === 0) {
      return;
    }

    await this.init();
    if (!this.store.isReady()) {
      return;
    }

    const snapshot = await this.getLookupSyncMetaSnapshot(tableNames);
    await this.store.setMetaBatch(
      Array.from(new Set(tableNames.map((tableName) => normalizeLookupName(tableName))))
        .map((tableName) => {
          const meta = snapshot[tableName];
          if (!meta) return null;

          return {
            tableName,
            count: meta.count,
            extras: {
              sourceModifiedEpochMs: meta.sourceModifiedEpochMs,
              lastSyncEpochMs: Date.now(),
              lastStatus: 'completed' as const,
            },
          };
        })
        .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
    );
  }

  /** Expose current effective lookup API base URL for service worker orchestration. */
  getEffectiveBaseUrl(): string {
    return this.baseUrl;
  }

  /**
   * Apply lookup rows fetched externally (for example by service worker) using the same refresh/metadata rules as
   * in-app sync.
   */
  async applyBackgroundLookupTable(
    tableName: string,
    rows: any[],
    options?: {
      modifiedDate?: string;
      forceRefresh?: boolean;
      deferMetaWrite?: boolean;
    }
  ): Promise<LookupSyncTableProgress> {
    const canonical = normalizeLookupName(tableName);

    if (!isRegisteredLookupTable(canonical)) {
      return {
        tableName: canonical,
        status: 'skipped_unregistered',
        reason: 'Lookup table is not registered in schema',
      };
    }

    if (!this.store.isEnabled()) {
      return {
        tableName: canonical,
        status: 'skipped_not_modified',
        reason: 'IndexedDB is disabled',
      };
    }

    await this.init();

    const cfg: LiveLookupConfig = {
      lookupConfigId: 0,
      lookupName: canonical,
      endpoint: canonical,
      labelColumn: 'description',
      isActive: true,
      modifiedDate: options?.modifiedDate,
    };

    const shouldRefresh = await this.shouldRefreshLookupTable(canonical, cfg, options?.forceRefresh ?? false);

    if (!shouldRefresh.refresh) {
      return {
        tableName: canonical,
        status: 'skipped_not_modified',
        reason: shouldRefresh.reason,
      };
    }

    const normalizedRows = this.normalizeLookupRows(canonical, rows ?? []);
    const now = Date.now();

    if (options?.deferMetaWrite) {
      await this.store.replaceTableData(canonical, normalizedRows);
      LookupService.global.deferredLookupMeta.set(canonical, {
        count: normalizedRows.length,
        sourceModifiedEpochMs: shouldRefresh.serverModifiedEpochMs,
        lastSyncEpochMs: now,
        lastStatus: 'completed',
      });
    } else {
      await this.store.replaceTableDataWithMeta(canonical, normalizedRows, {
        sourceModifiedEpochMs: shouldRefresh.serverModifiedEpochMs,
        lastSyncEpochMs: now,
        lastStatus: 'completed',
      });
    }

    LookupService.global.pendingRequests.delete(canonical);
    LookupService.global.memoryCache.delete(canonical);
    if (WORKER_SEARCH_TABLES[canonical]) {
      void this.refreshWorkerSearchRanges(canonical);
    }
    if (canonical === 'drug-codes') {
      LookupService.global.drugCodesSearchCache.clear();
    }

    return {
      tableName: canonical,
      status: 'completed',
      rowCount: normalizedRows.length,
    };
  }

  /**
   * Downloads a lookup table into IndexedDB only when needed: the table is empty locally, or the server's lookup-config
   * `modifiedDate` is newer than what was last synced. Used by on-demand download flows (e.g. assessment/appointment
   * offline download) for tables intentionally excluded from the recurring background sync (icd-codes, drug-codes,
   * clients, vendors — see LOOKUP_TABLE_SCOPE_RULES.excludeFromSync).
   */
  async refreshLookupTableIfStale(
    lookupName: string,
    lookupConfigs?: readonly LiveLookupConfig[]
  ): Promise<LookupSyncTableProgress> {
    const canonical = normalizeLookupName(lookupName);

    if (!isRegisteredLookupTable(canonical)) {
      return {
        tableName: canonical,
        status: 'skipped_unregistered',
        reason: 'Lookup table is not registered in schema',
      };
    }

    if (!this.store.isEnabled()) {
      return { tableName: canonical, status: 'skipped_not_modified', reason: 'IndexedDB is disabled' };
    }

    await this.init();
    if (!this.store.isReady()) {
      return { tableName: canonical, status: 'failed', reason: 'IndexedDB is not ready' };
    }

    const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
    const existingCount = this.store.tableExists(canonical) ? await this.store.count(canonical) : 0;

    if (!isOnline) {
      return existingCount === 0
        ? { tableName: canonical, status: 'skipped_offline', reason: 'Offline and no local rows' }
        : { tableName: canonical, status: 'skipped_not_modified', reason: 'Lookup config unavailable, using cache' };
    }

    let liveConfig: LiveLookupConfig | undefined;
    try {
      const configs = lookupConfigs ?? (await this.getLiveLookupConfigs());
      liveConfig = configs.find(
        (config) => normalizeLookupName(config.lookupName || config.endpoint || '') === canonical
      );
    } catch (error) {
      console.warn(`[LookupService] Failed to fetch lookup-configs for "${canonical}".`, error);
    }

    if (existingCount > 0 && !liveConfig) {
      // Either offline, or the config lookup failed while local rows already exist — keep serving cache
      // instead of forcing an unnecessary refetch.
      return { tableName: canonical, status: 'skipped_not_modified', reason: 'Lookup config unavailable, using cache' };
    }

    const effectiveConfig: LiveLookupConfig = liveConfig ?? {
      lookupConfigId: 0,
      lookupName: canonical,
      endpoint: canonical,
      labelColumn: 'description',
      isActive: true,
    };

    const shouldRefresh = await this.shouldRefreshLookupTable(canonical, effectiveConfig, false);
    if (!shouldRefresh.refresh) {
      return { tableName: canonical, status: 'skipped_not_modified', reason: shouldRefresh.reason };
    }

    try {
      const url = `${this.baseUrl}/api/Lookup/${canonical}`;
      const data = await firstValueFrom(this.http.get<any[]>(url));
      const normalizedRows = this.normalizeLookupRows(canonical, data ?? []);

      await this.store.replaceTableDataWithMeta(canonical, normalizedRows, {
        sourceModifiedEpochMs: shouldRefresh.serverModifiedEpochMs,
        lastSyncEpochMs: Date.now(),
        lastStatus: 'completed',
      });

      LookupService.global.pendingRequests.delete(canonical);
      LookupService.global.memoryCache.delete(canonical);
      if (WORKER_SEARCH_TABLES[canonical]) {
        void this.refreshWorkerSearchRanges(canonical);
      }
      if (canonical === 'drug-codes') {
        LookupService.global.drugCodesSearchCache.clear();
      }

      return { tableName: canonical, status: 'completed', rowCount: normalizedRows.length };
    } catch (error) {
      console.error(`[LookupService] refreshLookupTableIfStale failed for "${canonical}":`, error);
      if (existingCount > 0) {
        return { tableName: canonical, status: 'skipped_not_modified', reason: 'Refresh failed, serving cache' };
      }
      return {
        tableName: canonical,
        status: 'failed',
        reason: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /** Fetches the current lookup configuration snapshot for an explicit download operation. */
  async getLiveLookupConfigs(): Promise<LiveLookupConfig[]> {
    if (!this.baseUrl) {
      return [];
    }

    const rows = await firstValueFrom(
      this.http.get<LiveLookupConfig[]>(`${this.baseUrl}/api/Lookup/lookup-configs?activeOnly=true`, {
        headers: new HttpHeaders({ 'X-Skip-Loading': 'true' }),
      })
    );
    return rows ?? [];
  }

  /** Flush deferred lookup metadata updates in one _meta batch transaction. */
  async flushDeferredLookupMeta(): Promise<void> {
    const entries = Array.from(LookupService.global.deferredLookupMeta.entries());
    if (entries.length === 0) return;

    await this.store.setMetaBatch(
      entries.map(([tableName, meta]) => ({
        tableName,
        count: meta.count,
        extras: {
          sourceModifiedEpochMs: meta.sourceModifiedEpochMs,
          lastSyncEpochMs: meta.lastSyncEpochMs,
          lastStatus: meta.lastStatus,
        },
      }))
    );

    LookupService.global.deferredLookupMeta.clear();
  }

  /** Drop any deferred metadata that has not yet been flushed. */
  clearDeferredLookupMeta(): void {
    LookupService.global.deferredLookupMeta.clear();
  }

  /** Check if data exists */
  async hasData(lookupName: string): Promise<boolean> {
    lookupName = normalizeLookupName(lookupName);

    if (this.isLiveApiOnlyLookup(lookupName)) {
      const liveData = await this.fetchFromAPIFresh(lookupName);
      return liveData.length > 0;
    }

    await this.init();
    if (LookupService.global.initFailed || !this.store.isReady()) return false;
    if (!this.store.tableExists(lookupName)) return false;
    const count = await this.store.count(lookupName);
    return count > 0;
  }

  /** Force refresh data from API */
  async refresh(lookupName: string): Promise<any[]> {
    lookupName = normalizeLookupName(lookupName);

    if (this.isLiveApiOnlyLookup(lookupName)) {
      return this.fetchFromAPIFresh(lookupName);
    }

    await this.init();
    if (LookupService.global.initFailed) {
      throw new Error('[LookupService] Cannot refresh - service not initialized');
    }
    if (this.store.tableExists(lookupName)) {
      await this.store.clear(lookupName);
    }
    await this.store.deleteMeta(lookupName);
    LookupService.global.pendingRequests.delete(lookupName);
    LookupService.global.memoryCache.delete(lookupName);
    if (lookupName === 'drug-codes') {
      LookupService.global.drugCodesSearchCache.clear();
    }
    return this.get(lookupName);
  }

  /** Reset failure state and allow retry (call this to manually retry after fixing issues) */
  reset(): void {
    const g = LookupService.global;
    g.initFailed = false;
    g.initialized = false;
    g.retryCount = 0;
    g.memoryCache.clear();
    g.pendingRequests.clear();
    g.drugCodesSearchCache.clear();
  }

  /**
   * Set the base URL explicitly from an external injection context. Use this when a component that has access to
   * LOOKUP_API_BASE_URL in its injector hierarchy needs to ensure the service can initialize. This is needed in MFE
   * deployments where the service is created in the root (shell) injector but the token is only available in
   * component-level providers.
   */
  setBaseUrl(url: string): void {
    if (url) {
      LookupService.global.baseUrl = url;
    }
  }

  // ===========================================================================
  // Private - On-demand table creation & data loading
  // ===========================================================================

  private async ensureDataAndReturn<T>(
    lookupName: string,
    fn: () => Promise<T>,
    queryParams?: Record<string, string | number>
  ): Promise<T> {
    lookupName = normalizeLookupName(lookupName);

    if (this.isLiveApiOnlyLookup(lookupName)) {
      if (!this.baseUrl) {
        throw new Error(`[LookupService] No API base URL available. Cannot fetch lookup "${lookupName}".`);
      }
      if (queryParams && Object.keys(queryParams).length > 0) {
        return this.fetchFromAPI(lookupName, queryParams) as Promise<T>;
      }
      return this.fetchFromAPIFresh(lookupName) as Promise<T>;
    }

    // IndexedDB disabled - bypass cache entirely, just fetch from API
    // This avoids init() which requires configs that are only needed for caching
    if (!this.store.isEnabled()) {
      if (!this.baseUrl) {
        throw new Error(`[LookupService] No API base URL available. Cannot fetch lookup "${lookupName}".`);
      }
      if (queryParams && Object.keys(queryParams).length > 0) {
        return this.fetchFromAPI(lookupName, queryParams) as Promise<T>;
      }
      return this.fetchFromAPIWithoutParams(lookupName) as Promise<T>;
    }

    await this.init();

    const g = LookupService.global;

    // If init failed, attempt to serve from existing cache (offline resilience)
    if (g.initFailed || !g.initialized) {
      if (this.store.isReady() && this.store.tableExists(lookupName)) {
        const cachedCount = await this.store.count(lookupName);
        if (cachedCount > 0) {
          return fn();
        }
      }
      throw new Error(`[LookupService] Service not initialized. Lookup "${lookupName}" unavailable.`);
    }

    if (!this.isSupportedLookup(lookupName)) {
      console.error('[LookupService] Unknown lookup (not offline-registered or live-only):', lookupName);
      throw new Error(
        `Unknown lookup: ${lookupName}. Add it to LOOKUP_TABLES in schema-registry.ts or LIVE_API_ONLY_LOOKUPS in lookup.service.ts`
      );
    }

    // If query params provided, always fetch fresh from API (don't use cache)
    if (queryParams && Object.keys(queryParams).length > 0) {
      const data = await this.fetchFromAPI(lookupName, queryParams);
      return data as T;
    }

    // Table doesn't exist in schema - create it and load data
    if (!this.store.tableExists(lookupName)) {
      const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
      if (!isOnline) {
        return fn();
      }
      await this.createTableAndLoadData(lookupName);
      return fn();
    }

    if (this.isNetworkFirstLookup(lookupName)) {
      const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
      if (!isOnline) {
        return fn();
      }

      try {
        await this.createTableAndLoadData(lookupName, true);
        return fn();
      } catch (error) {
        const cachedCount = await this.store.count(lookupName);
        if (cachedCount > 0) {
          return fn();
        }
        throw error;
      }
    }

    // For cache-first lookups, if IndexedDB has any rows, always use cache and never
    // force refresh by expiry timestamp. API should only seed missing/empty tables.
    if (this.isIndexedDbFirstLookup(lookupName)) {
      const count = await this.store.count(lookupName);
      if (count > 0) {
        return fn();
      }

      const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
      if (isOnline) {
        await this.createTableAndLoadData(lookupName);
      }
      return fn();
    }

    // ponytail: serve cached lookup data when browser is offline
    const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
    if (!isOnline) {
      return fn();
    }

    // For non-cache-first lookups, refresh from API only when local table is empty.
    const cachedCount = await this.store.count(lookupName);
    if (cachedCount <= 0) {
      // No local data - clear and reload from API
      if (this.store.tableExists(lookupName)) {
        await this.store.clear(lookupName);
      }
      await this.createTableAndLoadData(lookupName);
    }

    return fn();
  }

  private tableExists(name: string): boolean {
    return this.store.tableExists(name);
  }

  private async createTableAndLoadData(lookupName: string, replaceExisting = false): Promise<void> {
    // Prevent duplicate requests
    if (LookupService.global.pendingRequests.has(lookupName)) {
      await LookupService.global.pendingRequests.get(lookupName);
      return;
    }

    const promise = this._createTableAndLoadData(lookupName, replaceExisting);
    LookupService.global.pendingRequests.set(lookupName, promise);
    try {
      await promise;
    } finally {
      LookupService.global.pendingRequests.delete(lookupName);
    }
  }

  private async _createTableAndLoadData(lookupName: string, replaceExisting = false): Promise<any[]> {
    // Check if table is registered in schema registry
    if (!isRegisteredLookupTable(lookupName)) {
      console.warn(
        `[LookupService] Table "${lookupName}" not in schema registry. ` +
          `Add it to LOOKUP_TABLES in schema-registry.ts and bump LOOKUP_SCHEMA_VERSION.`
      );
      // Fall back to API-only (no caching) for unregistered tables
      const url = `${this.baseUrl}/api/Lookup/${lookupName}`;
      const data = await firstValueFrom(this.http.get<any[]>(url));
      const normalizedData = this.normalizeLookupRows(lookupName, data || []);
      // Store in memory cache for subsequent calls in this session
      LookupService.global.memoryCache.set(lookupName, normalizedData);
      return normalizedData;
    }

    // 1. Fetch all data from API
    const url = `${this.baseUrl}/api/Lookup/${lookupName}`;
    const data = await firstValueFrom(this.http.get<any[]>(url));
    const normalizedData = this.normalizeLookupRows(lookupName, data || []);

    if (replaceExisting && this.store.tableExists(lookupName)) {
      await this.store.clear(lookupName);
    }

    if (!normalizedData.length) {
      if (replaceExisting) {
        await this.store.setMeta(lookupName, 0);
      }
      return [];
    }

    // 2. Get pre-registered schema from registry
    const registeredSchema = getLookupTableSchema(lookupName)!;

    // 3. Table already exists (registered at init) - just store data
    await this.store.bulkPut(lookupName, normalizedData);
    await this.store.setMeta(lookupName, normalizedData.length);

    if (WORKER_SEARCH_TABLES[lookupName]) {
      void this.refreshWorkerSearchRanges(lookupName);
    }
    if (lookupName === 'drug-codes') {
      const g = LookupService.global;
      g.drugCodesSearchCache.clear();
    }

    return normalizedData;
  }

  /**
   * Fetch data directly from API with query parameters (bypasses cache) Used for filtered lookups like
   * cpt-filters?interventionTypeID=1
   */
  private async fetchFromAPI(lookupName: string, queryParams: Record<string, string | number>): Promise<any[]> {
    const g = LookupService.global;
    const queryString = Object.entries(queryParams)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&');
    const cacheKey = `${lookupName}?${queryString}`;

    // Return cached result if available (avoids duplicate calls for same params)
    if (g.memoryCache.has(cacheKey)) {
      return g.memoryCache.get(cacheKey)!;
    }

    // Deduplicate concurrent requests with identical params
    if (g.pendingRequests.has(cacheKey)) {
      return g.pendingRequests.get(cacheKey)!;
    }

    const promise = (async () => {
      const url = `${this.baseUrl}/api/Lookup/${lookupName}?${queryString}`;
      const data = await firstValueFrom(this.http.get<any[]>(url));
      const result = this.normalizeLookupRows(lookupName, data || []);
      g.memoryCache.set(cacheKey, result);
      return result;
    })();

    g.pendingRequests.set(cacheKey, promise);
    try {
      return await promise;
    } finally {
      g.pendingRequests.delete(cacheKey);
    }
  }

  /**
   * Fetch from API without query params (for cache-bypass mode when IndexedDB is disabled). Uses in-memory cache to
   * avoid repeated API calls for the same lookup.
   */
  private async fetchFromAPIWithoutParams(lookupName: string): Promise<any[]> {
    const g = LookupService.global;

    // Check memory cache first
    if (g.memoryCache.has(lookupName)) {
      return g.memoryCache.get(lookupName)!;
    }

    // Deduplicate concurrent requests
    if (g.pendingRequests.has(lookupName)) {
      return g.pendingRequests.get(lookupName)!;
    }

    const promise = (async () => {
      const url = `${this.baseUrl}/api/Lookup/${lookupName}`;
      const data = await firstValueFrom(this.http.get<any[]>(url));
      const result = this.normalizeLookupRows(lookupName, data || []);

      // Store in memory cache
      g.memoryCache.set(lookupName, result);

      return result;
    })();

    g.pendingRequests.set(lookupName, promise);
    try {
      return await promise;
    } finally {
      g.pendingRequests.delete(lookupName);
    }
  }

  /** Always fetches fresh data from API with no persistent or in-memory cache. Used for large live-only lookup datasets. */
  private async fetchFromAPIFresh(lookupName: string): Promise<any[]> {
    const url = `${this.baseUrl}/api/Lookup/${lookupName}`;
    const data = await firstValueFrom(this.http.get<any[]>(url));
    return this.normalizeLookupRows(lookupName, data || []);
  }

  private parseUtcDateToEpoch(dateValue?: string): number | undefined {
    if (!dateValue) return undefined;

    const trimmed = dateValue.trim();
    if (!trimmed) return undefined;

    const hasTimezone = /(?:z|[+-]\d{2}:\d{2})$/i.test(trimmed);
    const normalized = hasTimezone ? trimmed : `${trimmed}Z`;
    const epoch = Date.parse(normalized);
    return Number.isNaN(epoch) ? undefined : epoch;
  }

  private async shouldRefreshLookupTable(
    tableName: string,
    liveConfig: LiveLookupConfig,
    forceRefresh: boolean
  ): Promise<{
    refresh: boolean;
    reason: string;
    serverModifiedEpochMs?: number;
  }> {
    if (forceRefresh) {
      return {
        refresh: true,
        reason: 'Force refresh requested',
        serverModifiedEpochMs: this.parseUtcDateToEpoch(liveConfig.modifiedDate ?? liveConfig.createdDate),
      };
    }

    const meta = await this.store.getMeta(tableName);
    const serverModifiedEpochMs = this.parseUtcDateToEpoch(liveConfig.modifiedDate ?? liveConfig.createdDate);

    if (!meta) {
      return {
        refresh: true,
        reason: 'Missing local metadata',
        serverModifiedEpochMs,
      };
    }

    // Some lookup tables (e.g. association-types) are legitimately empty server-side. Only force a
    // refresh for empty local data when we've never completed a successful sync for this table before
    // -- otherwise an empty table would be re-fetched on every load forever.
    if (meta.count === 0 && meta.lastStatus !== 'completed') {
      return {
        refresh: true,
        reason: 'No local rows',
        serverModifiedEpochMs,
      };
    }

    if (
      serverModifiedEpochMs != null &&
      (meta.sourceModifiedEpochMs == null || serverModifiedEpochMs > meta.sourceModifiedEpochMs)
    ) {
      return {
        refresh: true,
        reason: 'Server modified timestamp is newer',
        serverModifiedEpochMs,
      };
    }

    return {
      refresh: false,
      reason: 'Not modified',
      serverModifiedEpochMs,
    };
  }

  private normalizeLookupRows(lookupName: string, rows: any[]): any[] {
    if (!Array.isArray(rows)) {
      // drug-medication-details returns a single object, not an array — wrap it so callers can extract [0].
      if (rows !== null && rows !== undefined && typeof rows === 'object') {
        return [rows];
      }
      return [];
    }
    if (rows.length === 0) {
      return [];
    }

    if (lookupName === 'drug-codes') {
      // Canonicalize unique medication-name rows for IndexedDB search.
      return rows.map((row: any) => {
        const medicationName = row?.medicationName ?? row?.MedicationName ?? null;
        const drugCodeId = row?.drugCodeId ?? row?.DrugCodeId ?? row?.drugCodeID ?? row?.DrugCodeID ?? null;

        return {
          ...row,
          medicationName,
          drugCodeId,
        };
      });
    }

    // Canonicalize ICD HCC field so downstream logic and IndexedDB index use only hccCode.
    if (lookupName !== 'icd-codes') {
      return rows;
    }

    return rows.map((row: any) => {
      const hccCode = row?.hccCode ?? row?.HccCode ?? null;
      const code = String(row?.code ?? row?.Code ?? '')
        .replace(/\s+/g, '')
        .toUpperCase();
      return {
        ...row,
        code,
        hccCode,
      };
    });
  }

  /** Converts lookup-specific business rules into the serializable worker search protocol. */
  private getWorkerSearchConfig(
    lookupName: string,
    primaryKey: string | undefined,
    filterContext?: LookupSearchFilter
  ): LookupWorkerSearchConfig | null {
    if (!primaryKey) return null;

    const tableConfig = WORKER_SEARCH_TABLES[lookupName];
    if (!tableConfig) return null;

    return {
      tableName: lookupName,
      primaryKey,
      chunkSize: tableConfig.chunkSize,
      rangeStrategy: tableConfig.rangeStrategy,
      searchFields: tableConfig.searchFields,
      conditions: tableConfig.getConditions?.(filterContext),
    };
  }

  private async refreshWorkerSearchRanges(lookupName: string): Promise<void> {
    const schema = getLookupTableSchema(lookupName);
    const config = this.getWorkerSearchConfig(lookupName, schema?.primaryKey);
    if (!config) return;

    try {
      await this.lookupSearchCoordinator.refreshRanges(config);
    } catch (error) {
      console.warn(`[LookupService] Unable to refresh worker search ranges for "${lookupName}":`, error);
    }
  }

  private normalizeHccForCompare(value: unknown): string | null {
    if (value == null) return null;

    const raw = String(value).trim();
    if (!raw) return null;

    const numeric = Number(raw);
    if (!Number.isNaN(numeric)) {
      // Numeric compare key so 55 and 55.0 resolve to the same value.
      return `n:${numeric}`;
    }

    return `s:${raw.toLowerCase()}`;
  }

  private hccMatches(itemHcc: unknown, requestedHcc: unknown): boolean {
    const itemKey = this.normalizeHccForCompare(itemHcc);
    const requestedKey = this.normalizeHccForCompare(requestedHcc);
    return itemKey != null && requestedKey != null && itemKey === requestedKey;
  }
}
